import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider.jsx';
import { cartApi } from '../lib/cart/cartApi.js';
import { brandFromReturnUrl } from '../lib/brand/brandFromReturnUrl.js';

export default function OrderConfirmationPage() {
  const [params] = useSearchParams();
  const sessionId = Number(params.get('session') || '0');
  const { accessToken } = useAuth();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    if (!sessionId || !accessToken) return;
    cartApi.pollCheckoutSession(sessionId, accessToken)
      .then(setSession)
      .catch((e) => setError(e?.detail?.errors?.[0]?.message || 'Could not load order.'));
  }, [sessionId, accessToken]);

  const brand = brandFromReturnUrl(session?.return_to_url);

  // Auto-redirect to brand site once the session loads + payment is paid.
  useEffect(() => {
    if (!session || session.status !== 'paid' || !brand?.returnUrl) return;
    if (countdown <= 0) {
      window.location.href = brand.returnUrl;
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [session, brand, countdown]);

  const goNow = () => {
    if (brand?.returnUrl) window.location.href = brand.returnUrl;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between border-b border-slate-200/60 bg-white/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-superhomes-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">S</div>
          <span className="font-bold text-slate-900 tracking-tight">SuperHomes Checkout</span>
        </div>
        {brand && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Paid via</div>
            <div className="text-sm font-semibold text-slate-700">{brand.name}</div>
          </div>
        )}
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-900/5 p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-5">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Payment received</h1>
            <p className="text-slate-500 mt-2 leading-relaxed">
              Thank you. We've sent a receipt to your email and your order is being prepared.
            </p>

            {error && (
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">
                {error}
              </div>
            )}

            {session && (
              <div className="mt-7 text-left">
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                  <div className="px-4 py-3 flex justify-between items-center">
                    <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Reference</span>
                    <span className="font-mono text-sm text-slate-700">#{session.id}</span>
                  </div>
                  {session.razorpay_payment_id && (
                    <div className="px-4 py-3 flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wider text-slate-400 font-bold">Payment ID</span>
                      <span className="font-mono text-xs text-slate-700">{session.razorpay_payment_id}</span>
                    </div>
                  )}
                  <div className="px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2">Orders</div>
                    <ul className="space-y-1.5">
                      {session.orders.map((o) => (
                        <li key={o.id} className="text-sm flex justify-between items-center">
                          <span className="text-slate-700">Order #{o.id}</span>
                          <PaymentChip status={o.payment_status} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {brand?.returnUrl && session?.status === 'paid' && (
              <div className="mt-7">
                <button
                  onClick={goNow}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl py-3.5 transition flex items-center justify-center gap-2"
                >
                  Return to {brand.name}
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <path d="M5 10h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <p className="text-xs text-slate-400 mt-3">
                  Auto-redirecting in {countdown}s…
                </p>
              </div>
            )}

            {!brand?.returnUrl && (
              <div className="mt-7">
                <Link to="/" className="text-superhomes-500 font-semibold">← Back to SuperHomes</Link>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Receipt sent to your registered email. Questions? Reply to that email.
          </p>
        </div>
      </main>
    </div>
  );
}

function PaymentChip({ status }) {
  const map = {
    paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    partial: { label: 'Partial', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
    unpaid: { label: 'Pending', cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  };
  const v = map[status] || map.unpaid;
  return (
    <span className={`text-[11px] font-bold uppercase tracking-wider ring-1 rounded-full px-2 py-0.5 ${v.cls}`}>
      {v.label}
    </span>
  );
}
